import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

const links = {
  github: "https://github.com/kanansinghal",
  linkedin: "https://www.linkedin.com/in/kanan-singhal-174a62263/",
  leetcode: "https://leetcode.com/u/KananSinghal/",
  codeforces: "https://codeforces.com/profile/optimus_maximus",
  email: "mailto:kanansinghal01@gmail.com",
};

const featured = [
  {
    id: "01",
    title: "DurableFlow",
    type: "SYSTEMS",
    color: "cobalt",
    note: "The workflow keeps going after the worker does not.",
    description:
      "A durable employee-onboarding engine coordinating payroll, access, training, and equipment tasks in dependency order. Leases, retries, PostgreSQL SKIP LOCKED, and idempotency protect every step.",
    proof: ["20 concurrent workers", "Crash recovery", "No duplicate effects"],
    stack: ["PERN", "TypeScript", "PostgreSQL", "Docker", "SSE"],
    source: "https://github.com/KananSinghal/Durable-Flow",
    live: "https://durableflow.onrender.com/",
    art: "flow",
  },
  {
    id: "02",
    title: "B+ Tree Store",
    type: "C++ / STORAGE",
    color: "tomato",
    note: "One million records, without pretending memory is infinite.",
    description:
      "A disk-backed key-value store with logarithmic CRUD, ordered range scans, page splitting and merging, and a 16 MB LRU cache to avoid repeated disk reads.",
    proof: ["1M records", "100K randomized operations", "AddressSanitizer clean"],
    stack: ["C++", "OOP", "B+ Tree", "LRU", "ASan"],
    source: "https://github.com/KananSinghal/btree-kv-store",
    art: "tree",
  },
  {
    id: "03",
    title: "FoodTrace",
    type: "GRAPH / INTEGRITY",
    color: "moss",
    note: "A QR label should prove where it came from.",
    description:
      "A food-supply integrity system where signed batch hashes identify fake or tampered labels. TigerGraph traces lineage, contamination paths, complaints, and repeat offenders.",
    proof: ["Valid / Tampered / Fake", "Graph lineage", "Immutable batch proofs"],
    stack: ["Next.js", "Node.js", "TigerGraph", "Solidity", "SHA-256"],
    source: "https://github.com/KananSinghal/trueseal",
    art: "trace",
  },
  {
    id: "04",
    title: "WebRTC Workspace",
    type: "WEBRTC / REAL TIME",
    color: "lavender",
    note: "One canvas. Several cursors. No waiting for refresh.",
    description:
      "A peer-to-peer collaborative workspace for drawing, notes, and images. Cross-client synchronization keeps concurrent edits, undo, and redo consistent.",
    proof: ["Low-latency P2P", "Cross-client sync", "Consistent undo / redo"],
    stack: ["WebRTC", "Socket.io", "Node.js", "Canvas API", "JavaScript"],
    source: "https://github.com/khushibansal2/white-board",
    live: "https://whiteboardrtc.onrender.com/",
    art: "canvas",
  },
  {
    id: "05",
    title: "Disaster Assessment",
    type: "DTU RESEARCH / SNN",
    color: "yellow",
    note: "Damage is local. A city-level response has to coordinate it.",
    description:
      "DTU research combining U-Net and EfficientNet-B3 damage segmentation with a LIF spiking neural network for temporal seismic classification. Two hundred building agents coordinate into a city-level risk estimate.",
    proof: ["Mesa building agents", "95% accuracy", "0.91 macro F1"],
    stack: ["Python", "U-Net", "EfficientNet-B3", "Brian2", "Mesa"],
    source: "https://github.com/KananSinghal/snn-mas",
    art: "disaster",
  },
];

const projects = [
  {
    year: "2026",
    title: "DurableFlow",
    category: "Systems",
    one: "Crash-recoverable workflow execution with 20 concurrent workers.",
    stack: "PERN · TypeScript · Docker · SSE",
    link: "https://github.com/KananSinghal/Durable-Flow",
  },
  {
    year: "2026",
    title: "FoodTrace",
    category: "Full Stack",
    one: "Cryptographic food-label verification and graph-based batch lineage.",
    stack: "Next.js · Solidity · TigerGraph",
    link: "https://github.com/KananSinghal/trueseal",
  },
  {
    year: "2026",
    title: "WebRTC Collaborative Workspace",
    category: "Systems",
    one: "A synchronized peer-to-peer canvas with notes, images, and undo/redo.",
    stack: "WebRTC · Socket.io · Node.js",
    link: "https://github.com/khushibansal2/white-board",
  },
  {
    year: "2026",
    title: "Quark / Gluon Jet Classification",
    category: "Applied ML",
    one: "Rest-frame particle observables over 500K Pythia constituent arrays.",
    stack: "Python · Pythia 8 · BDT · Random Forest",
    link: "https://github.com/KananSinghal/Hepsim",
  },
  {
    year: "2026",
    title: "NGA Painting Retrieval",
    category: "Applied ML",
    one: "Content-aware artwork search using visual, pose, and face similarity.",
    stack: "CLIP · FAISS · MediaPipe · PyTorch",
    link: "https://github.com/KananSinghal/nga-painting-similarity",
  },
  {
    year: "2026",
    title: "B+ Tree Key-Value Store",
    category: "Systems",
    one: "A persistent C++ storage engine with range scans and an LRU page cache.",
    stack: "C++ · B+ Tree · AddressSanitizer",
    link: "https://github.com/KananSinghal/btree-kv-store",
  },
  {
    year: "2026",
    title: "WikiArt Multi-Label Classifier",
    category: "Applied ML",
    one: "Style, genre, and artist prediction through three independent heads.",
    stack: "EfficientNetV2 · Bi-GRU · PyTorch",
    link: "https://github.com/KananSinghal/ArtGAN",
  },
  {
    year: "2025",
    title: "Rockfall Prediction",
    category: "Applied ML",
    one: "Safety-focused evaluation across a 14-feature geospatial mine matrix.",
    stack: "XGBoost · GeoPandas · Rasterio",
    link: "https://github.com/KananSinghal/Rockfall_prediction",
  },
  {
    year: "2025",
    title: "EchoScribe",
    category: "Full Stack",
    one: "Searchable voice notes with automated transcription and tagged storage.",
    stack: "React · Node.js · Whisper · MongoDB",
    link: "https://github.com/KananSinghal/EchoScribe",
  },
  {
    year: "2025",
    title: "NeuroCity Resilience",
    category: "Research",
    one: "Two hundred communicating agents for city-level disaster assessment.",
    stack: "Mesa · Brian2 · U-Net · EfficientNet",
    link: "https://github.com/KananSinghal/snn-mas",
  },
  {
    year: "2025",
    title: "Financial Fraud Detection",
    category: "Research",
    one: "Published anomaly-detection work on imbalanced financial transactions.",
    stack: "XGBoost · Random Forest · LSTM",
    link: "https://doi.org/10.1007/978-3-032-28379-5_7",
  },
];

const awards = [
  {
    code: "A/01",
    big: "TOP 3,000",
    small: "from 134,000+",
    title: "Amazon ML Summer School 2026",
    color: "tomato",
  },
  {
    code: "A/02",
    big: "EXPERT",
    small: "1700+ rating",
    title: "Codeforces",
    color: "cobalt",
  },
  {
    code: "A/03",
    big: "1ST",
    small: "1,000+ teams",
    title: "AILean Hackathon 2026",
    color: "moss",
  },
  {
    code: "A/04",
    big: "700+",
    small: "problems solved",
    title: "LeetCode",
    color: "lavender",
  },
  {
    code: "A/05",
    big: "SELECTED",
    small: "50 students",
    title: "Cisco Women in Tech Mentorship 2026",
    color: "sky",
  },
  {
    code: "A/06",
    big: "PUBLISHED",
    small: "Springer LNNS · ICAICS 2025",
    title: "Financial Fraud Detection using Machine Learning Models",
    color: "lavender",
    link: "https://doi.org/10.1007/978-3-032-28379-5_7",
  },
  {
    code: "A/07",
    big: "STATE",
    small: "10m air pistol",
    title: "Delhi Shooting Championship",
    color: "yellow",
  },
];

const filters = ["All", "Systems", "Full Stack", "Applied ML", "Research"];

function FlowArt() {
  return (
    <div className="project-art flow-art" aria-hidden="true">
      <span className="flow-label">ONBOARD_#1842</span>
      <div className="flow-node node-a">START</div>
      <div className="flow-node node-b">PAYROLL</div>
      <div className="flow-node node-c">ACCESS</div>
      <div className="flow-node node-d">TRAINING</div>
      <i className="route r1" />
      <i className="route r2" />
      <i className="route r3" />
      <b className="lease">LEASE / 28s</b>
    </div>
  );
}

function TreeArt() {
  return (
    <div className="project-art tree-art" aria-hidden="true">
      <div className="disk">DISK</div>
      <span className="key root-key">42</span>
      <span className="key k1">16</span>
      <span className="key k2">71</span>
      <span className="key k3">08</span>
      <span className="key k4">23</span>
      <span className="key k5">59</span>
      <span className="key k6">84</span>
      <div className="cache-note">LRU / 16 MB</div>
    </div>
  );
}

function TraceArt() {
  return (
    <div className="project-art trace-art" aria-hidden="true">
      <div className="trace-card manufacturer">MFG<br />DL-07</div>
      <div className="trace-card batch">BATCH<br />#0041</div>
      <div className="trace-card store">NODE<br />NCR-12</div>
      <i className="trace-line tl1" />
      <i className="trace-line tl2" />
      <span className="qr">
        <i /><i /><i /><i /><i /><i /><i /><i /><i />
      </span>
      <b className="verified">✓ VERIFIED</b>
    </div>
  );
}

function CanvasArt() {
  return (
    <div className="project-art canvas-art" aria-hidden="true">
      <span className="canvas-toolbar"><i /><i /><i /><i /></span>
      <div className="canvas-note note-one">API PLAN<br />✓ sync events</div>
      <div className="canvas-note note-two">SHIP IT</div>
      <svg className="canvas-stroke" viewBox="0 0 500 300" role="presentation">
        <path d="M28 190 C105 64 176 271 258 119 S405 58 472 170" />
      </svg>
      <span className="cursor cursor-one">Kanan</span>
      <span className="cursor cursor-two">Peer 02</span>
      <b className="latency">P2P / LIVE</b>
    </div>
  );
}

function DisasterArt() {
  return (
    <div className="project-art disaster-art" aria-hidden="true">
      <div className="city-block block-one"><span>01</span><i /></div>
      <div className="city-block block-two"><span>02</span><i /></div>
      <div className="city-block block-three"><span>03</span><i /></div>
      <div className="city-block block-four"><span>04</span><i /></div>
      <svg className="agent-network" viewBox="0 0 500 340" role="presentation">
        <path d="M90 215 L210 110 L322 225 L416 105 M90 215 L322 225 M210 110 L416 105" />
      </svg>
      <span className="risk-pulse pulse-one" />
      <span className="risk-pulse pulse-two" />
      <div className="risk-meter"><b>CITY RISK</b><span><i /></span><small>0.74 / ALERT</small></div>
      <div className="research-stamp">DTU<br />SNN + MAS</div>
    </div>
  );
}

function ProjectArt({ type }) {
  if (type === "tree") return <TreeArt />;
  if (type === "trace") return <TraceArt />;
  if (type === "canvas") return <CanvasArt />;
  if (type === "disaster") return <DisasterArt />;
  return <FlowArt />;
}

function Header({ openArchive }) {
  return (
    <header className="site-header">
      <a className="monogram" href="#top" aria-label="Kanan Singhal home">
        <span>K</span><i /><span>S</span>
      </a>
      <nav aria-label="Main navigation">
        <a href="#work">Selected work</a>
        <button type="button" onClick={openArchive}>All projects</button>
        <a href="#achievements">Proof</a>
        <a href="#contact">Contact</a>
      </nav>
      <a className="resume-button" href="/kanansinghalcv.pdf" target="_blank" rel="noreferrer">
        Résumé <span>↗</span>
      </a>
    </header>
  );
}

function PlatformLinks() {
  return (
    <div className="platforms" aria-label="Coding profiles">
      <a className="platform-card codeforces-card" href={links.codeforces} target="_blank" rel="noreferrer">
        <span>CF</span>
        <strong>EXPERT</strong>
        <em>1700+</em>
        <b>Codeforces ↗</b>
      </a>
      <a className="platform-card leetcode-card" href={links.leetcode} target="_blank" rel="noreferrer">
        <span>LC</span>
        <strong>700+</strong>
        <em>QUESTIONS</em>
        <b>LeetCode ↗</b>
      </a>
      <a className="platform-card github-card" href={links.github} target="_blank" rel="noreferrer">
        <span>GH</span>
        <strong>BUILD</strong>
        <em>IN PUBLIC</em>
        <b>GitHub ↗</b>
      </a>
    </div>
  );
}

function Hero() {
  const [photoLoaded, setPhotoLoaded] = useState(false);

  return (
    <section className="hero" id="top">
      <div className="hero-left">
        <div className="edition-line">
          <span>Portfolio No. 02</span>
          <span>New Delhi / 2026</span>
        </div>
        <p className="hero-eyebrow">Software engineer · systems builder</p>
        <h1>
          <span>I MAKE</span>
          <span className="serif-line">systems survive</span>
          <span>THE MESSY PARTS.</span>
        </h1>
        <p className="hero-intro">
          I build backend systems, storage engines, and technical products that
          keep their state when workers crash, data grows, or the real world
          refuses to behave like a demo.
        </p>
        <PlatformLinks />
      </div>

      <div className="hero-workbench" aria-label="Interactive portrait workbench">
        <div className="portrait-board">
          <div className="board-ruler">
            <span>0</span><span>10</span><span>20</span><span>30</span>
          </div>
          <div className="portrait-fallback" aria-hidden={photoLoaded}>
            <b>KS</b><span>PORTRAIT SLOT</span>
          </div>
          <img
            className={photoLoaded ? "portrait loaded" : "portrait"}
            src="/kanan-cutout.png"
            alt="Kanan Singhal"
            onLoad={() => setPhotoLoaded(true)}
            onError={() => setPhotoLoaded(false)}
          />
          <span className="portrait-caption">FIG. 01 / KANAN SINGHAL</span>
        </div>
        <div className="floating-card card-cpp"><b>C++</b><span>CONTROL</span></div>
        <div className="floating-card card-ts"><b>TS</b><span>PRODUCT</span></div>
        <div className="floating-card card-py"><b>PY</b><span>MODELS</span></div>
        <div className="workbench-note">
          <span>NOW</span>
          <p>Building durable software + learning the internals behind it.</p>
        </div>
      </div>

      <div className="scroll-note" aria-hidden="true">
        <span>SCROLL</span><i />
      </div>
    </section>
  );
}

function FeaturedWork({ openArchive }) {
  return (
    <section className="featured-section" id="work">
      <div className="section-mast">
        <p>Selected work / five case files</p>
        <h2>Not project cards.<br /><em>Working evidence.</em></h2>
        <button className="archive-trigger" type="button" onClick={openArchive}>
          Open the complete project archive <span>11 projects ↗</span>
        </button>
      </div>

      <div className="case-list">
        {featured.map((project) => (
          <article className={`case-file ${project.color}`} key={project.title}>
            <div className="case-spine">
              <span>{project.id}</span>
              <small>{project.type}</small>
            </div>
            <div className="case-copy">
              <p className="case-note">{project.note}</p>
              <h3>{project.title}</h3>
              <p className="case-description">{project.description}</p>
              <ul className="proof-list">
                {project.proof.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <div className="case-stack">
                {project.stack.map((item) => <span key={item}>{item}</span>)}
              </div>
              <div className="case-links">
                <a href={project.source} target="_blank" rel="noreferrer">Source ↗</a>
                {project.live && <a href={project.live} target="_blank" rel="noreferrer">Live ↗</a>}
              </div>
            </div>
            <ProjectArt type={project.art} />
          </article>
        ))}
      </div>
    </section>
  );
}

function Achievements() {
  return (
    <section className="achievements-section" id="achievements">
      <div className="achievement-heading">
        <span>04 / FIELD NOTES</span>
        <h2>Things that happened<br />outside the editor.</h2>
        <p>
          Competitive programming, research, hackathons, mentorship, and
          shooting all ask for the same thing: stay calm, measure, adjust.
        </p>
      </div>
      <div className="award-wall">
        {awards.map((award, index) => (
          <article className={`award-card ${award.color}`} key={award.code}>
            <span className="award-code">{award.code}</span>
            <small>{award.small}</small>
            <strong>{award.big}</strong>
            {award.link ? (
              <a href={award.link} target="_blank" rel="noreferrer">
                <h3>{award.title} ↗</h3>
              </a>
            ) : <h3>{award.title}</h3>}
            <i>{String(index + 1).padStart(2, "0")}</i>
          </article>
        ))}
      </div>
    </section>
  );
}

function Workbench() {
  const groups = [
    {
      name: "Programming Languages",
      short: "LANG",
      tone: "tomato",
      items: ["C++", "Python"],
    },
    {
      name: "Full Stack Development",
      short: "FULL STACK",
      tone: "yellow",
      items: ["MERN", "HTML", "CSS", "JavaScript", "TypeScript", "FastAPI", "Flask", "RESTful APIs", "WebSockets"],
    },
    {
      name: "Databases & Tools",
      short: "DATA / OPS",
      tone: "cobalt",
      items: ["PostgreSQL", "MongoDB", "MySQL", "TigerGraph", "SQL", "Git", "Docker"],
    },
    {
      name: "CS Fundamentals",
      short: "CORE",
      tone: "moss",
      items: ["Data Structures & Algorithms", "OOP", "DBMS", "System Design", "Operating Systems", "Computer Networks"],
    },
    {
      name: "ML & Data Science",
      short: "ML / DATA",
      tone: "lavender",
      items: ["scikit-learn", "TensorFlow", "Pandas", "NumPy", "Matplotlib", "OpenCV"],
    },
  ];

  return (
    <section className="tool-section">
      <div className="tool-title">
        <span>05 / TECH INVENTORY</span>
        <h2>The stack<br /><em>I actually use.</em></h2>
        <p>
          Languages, frameworks, tools, and fundamentals from my current
          résumé. Hover over any rail to inspect it.
        </p>
      </div>
      <div className="skill-console">
        {groups.map((group, index) => {
          let movingItems = group.items;
          if (group.items.length < 4) {
            movingItems = [...group.items, ...group.items, ...group.items];
          }

          return (
            <article className={`skill-rail ${group.tone}`} key={group.name}>
              <div className="skill-label">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <small>{group.short}</small>
                  <h3>{group.name}</h3>
                </div>
                <b>{group.items.length}</b>
              </div>
              <div className="skill-window">
                <div
                  className={`skill-track ${index % 2 === 1 ? "reverse" : ""}`}
                  style={{ "--rail-speed": `${24 + group.items.length * 2}s` }}
                >
                  {[0, 1].map((copy) => (
                    <div className="skill-group" aria-hidden={copy === 1} key={copy}>
                      {movingItems.map((item, itemIndex) => (
                        <span className="skill-chip" key={`${copy}-${item}-${itemIndex}`}>
                          <i>{String((itemIndex % group.items.length) + 1).padStart(2, "0")}</i>
                          <b>{item}</b>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="contact-section" id="contact">
      <div className="contact-stamp">
        <span>AVAILABLE</span><b>FOR 2027</b><small>SWE INTERNSHIPS</small>
      </div>
      <p>Have an ambitious system, internship, or technical problem?</p>
      <h2>Send the difficult part.</h2>
      <a className="email-link" href={links.email}>
        kanansinghal01@gmail.com <span>↗</span>
      </a>
      <div className="contact-links">
        <a href={links.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
        <a href={links.github} target="_blank" rel="noreferrer">GitHub</a>
        <a href={links.codeforces} target="_blank" rel="noreferrer">Codeforces</a>
        <a href={links.leetcode} target="_blank" rel="noreferrer">LeetCode</a>
      </div>
    </section>
  );
}

function ProjectArchive({ close }) {
  const [filter, setFilter] = useState("All");
  const visible = useMemo(
    () => filter === "All" ? projects : projects.filter((project) => project.category === filter),
    [filter],
  );

  useEffect(() => {
    document.body.classList.add("archive-open");
    return () => document.body.classList.remove("archive-open");
  }, []);

  return (
    <div className="archive-view" role="dialog" aria-modal="true" aria-label="All projects">
      <header className="archive-header">
        <div>
          <span>PROJECT ARCHIVE</span>
          <b>11 entries / 2025—2026</b>
        </div>
        <button type="button" onClick={close}>Close archive ×</button>
      </header>
      <div className="archive-intro">
        <p>Every project, including the experiments that sit behind the selected work.</p>
        <h2>THE WHOLE<br /><em>WORK TABLE.</em></h2>
      </div>
      <div className="archive-filters" aria-label="Filter projects">
        {filters.map((item) => (
          <button
            type="button"
            className={filter === item ? "active" : ""}
            onClick={() => setFilter(item)}
            key={item}
          >
            {item}
          </button>
        ))}
      </div>
      <div className="archive-grid">
        {visible.map((project, index) => (
          <a href={project.link} target="_blank" rel="noreferrer" key={project.title}>
            <span className="archive-number">{String(index + 1).padStart(2, "0")}</span>
            <div>
              <small>{project.year} / {project.category}</small>
              <h3>{project.title}</h3>
              <p>{project.one}</p>
              <b>{project.stack}</b>
            </div>
            <i>↗</i>
          </a>
        ))}
      </div>
      <button className="archive-bottom-close" type="button" onClick={close}>Return to portfolio ↑</button>
    </div>
  );
}

function App() {
  const [archiveOpen, setArchiveOpen] = useState(window.location.hash === "#archive");

  const openArchive = () => {
    window.history.replaceState(null, "", "#archive");
    setArchiveOpen(true);
  };

  const closeArchive = () => {
    window.history.replaceState(null, "", window.location.pathname);
    setArchiveOpen(false);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && archiveOpen) closeArchive();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [archiveOpen]);

  return (
    <>
      <Header openArchive={openArchive} />
      <main>
        <Hero />
        <div className="ticker" aria-hidden="true">
          <span>BACKEND SYSTEMS</span><i>✳</i><span>STORAGE ENGINES</span><i>✳</i>
          <span>DURABLE EXECUTION</span><i>✳</i><span>APPLIED ML</span><i>✳</i>
          <span>BACKEND SYSTEMS</span><i>✳</i><span>STORAGE ENGINES</span>
        </div>
        <FeaturedWork openArchive={openArchive} />
        <Achievements />
        <Workbench />
        <Contact />
      </main>
      <footer className="site-footer">
        <span>© 2026 Kanan Singhal</span>
        <span>Built in New Delhi · no template</span>
        <a href="#top">Back to top ↑</a>
      </footer>
      {archiveOpen && <ProjectArchive close={closeArchive} />}
    </>
  );
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
